import { type FC, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { Alert, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useVerifyEmail } from "@/partner/entities/auth";
import { ErrorMessage } from "@/shared/components";

interface VerifyEmailFormProps {
  token: string;
  onCompleted: (data: any) => void;
  onGoBack?: () => void;
  contactInfo: string;
  contactType: "email";
}

type ContactType = "email" | "sms";

// contactType сейчас только "email" в проде, но ветка "sms" уже написана в
// getDescription()/label — мапа заведена по полному union на будущее (см.
// инвентаризацию, VerifyEmailForm.tsx).
const VERIFY_DESCRIPTION_KEYS: Record<ContactType, string> = {
  email: "auth.verify.descriptionEmail",
  sms: "auth.verify.descriptionSms",
};

const VERIFY_CODE_LABEL_KEYS: Record<ContactType, string> = {
  email: "auth.verify.codeLabelEmail",
  sms: "auth.verify.codeLabelSms",
};

export const VerifyEmailForm: FC<VerifyEmailFormProps> = ({
  token,
  onCompleted,
  onGoBack,
  contactInfo,
  contactType,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [timeLeft, setTimeLeft] = useState(600); // 10 минут в секундах

  const [verifyEmail, { loading, error }] = useVerifyEmail({
    variables: { code, token },
    onCompleted,
  });

  // Таймер обратного отсчета
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    verifyEmail();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value.trim();
    setCode(code);

    if (code.length === 6) {
      verifyEmail({
        variables: {
          code,
          token,
        },
      });
    }
  };

  const getDescription = () =>
    t(VERIFY_DESCRIPTION_KEYS[contactType], { contact: contactInfo });

  // Форматирование времени в MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Typography variant="body2">{getDescription()}</Typography>

        <ErrorMessage error={error} />

        <TextField
          autoFocus
          required
          fullWidth
          variant="outlined"
          label={t(VERIFY_CODE_LABEL_KEYS[contactType])}
          placeholder="******"
          disabled={loading || timeLeft === 0}
          onChange={onChange}
          value={code}
        />

        {timeLeft > 0 ? (
          <Alert severity="info">
            <Typography variant="body2">
              <Trans
                i18nKey="auth.verify.codeValidFor"
                values={{ time: formatTime(timeLeft) }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          </Alert>
        ) : (
          <Alert
            severity="warning"
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={onGoBack}
                disabled={loading}
              >
                {t("auth.verify.resendCode")}
              </Button>
            }
          >
            <Typography variant="body2">
              {t("auth.verify.codeExpired")}
            </Typography>
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={code.length < 6 || loading || timeLeft === 0}
        >
          {t("auth.verify.submit")}
        </Button>

        {onGoBack && (
          <Button
            fullWidth
            variant="text"
            color="secondary"
            onClick={onGoBack}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            {t("common.actions.back")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
